import { Badge, Tag, Tooltip, Typography } from "antd";
import ValidationStatusBadge from "../../util-components/ValidationStatusBadge";
import { siteStatusTag } from "../../util-components/siteStatusTag";
import { phaseRotationDeclarations } from "../../../util/phaseRotations";
import { SiteValidationBadge } from "./SiteValidationBadge";
import type { BoardSite } from "./boardColumns";
import { formatDateTime } from "../../../util/formatDate";

const { Text } = Typography;

type SiteCardProps = {
  boardSite: BoardSite;
  partnerName?: string;
  onClick: () => void;
};

const CardRow: React.FC<{ label: string; children: React.ReactNode }> = ({
  label,
  children,
}) => (
  <div className="flex items-center gap-2">
    <Text type="secondary" className="w-28 shrink-0 text-xs">
      {label}
    </Text>
    {children}
  </div>
);

export const SiteCard: React.FC<SiteCardProps> = ({
  boardSite: { site, phasePermutationMode },
  partnerName,
  onClick,
}) => {
  const statusTag = siteStatusTag[site.status ?? "inactive"];
  const validationStatus = site.validation_status;
  const siteConfiguration = site.configurations?.site_configuration;
  const { undeclared } = phaseRotationDeclarations(
    siteConfiguration,
    site.configurations?.io_configuration,
  );

  return (
    <div
      className="rounded border border-gray-200 bg-white p-3 shadow-sm hover:cursor-pointer hover:shadow-md"
      onClick={onClick}
    >
      <div className="flex items-start justify-between gap-2">
        <Text strong className="break-all leading-tight">
          {site.name}
        </Text>
        <Tag color={statusTag.color} className="m-0">
          {statusTag.title}
        </Tag>
      </div>
      <div>
        <Text
          type="secondary"
          className="text-xs break-all"
          copyable={{ text: site.site_id }}
          onClick={(event) => event.stopPropagation()}
        >
          {site.site_id}
        </Text>
        {partnerName && (
          <Text type="secondary" className="text-xs">
            {" · "}
            {partnerName}
          </Text>
        )}
      </div>

      <div className="mt-1.5 flex flex-col gap-1 border-t border-gray-100 pt-1.5">
        <CardRow label="Validation">
          <SiteValidationBadge validationStatus={validationStatus} />
        </CardRow>
        <CardRow label="Ingress">
          <ValidationStatusBadge
            status={validationStatus?.ingress_status?.status}
            label="Ingress Status"
            ingressStatus={validationStatus?.ingress_status ?? undefined}
          />
        </CardRow>
        <CardRow label="Configuration">
          <ValidationStatusBadge
            status={validationStatus?.site_configuration_status?.status}
            label="Configuration Status"
            configurationStatus={
              validationStatus?.site_configuration_status ?? undefined
            }
          />
        </CardRow>
        <CardRow label="Phase rotations">
          {!siteConfiguration ? (
            <Badge status="default" text="No configuration" />
          ) : undeclared.length === 0 ? (
            <Badge status="success" text="OK" />
          ) : (
            <Tooltip
              title={`No phase rotation declared on: ${undeclared.join(", ")}`}
            >
              <Badge status="warning" text={`${undeclared.length} missing`} />
            </Tooltip>
          )}
        </CardRow>
        <CardRow label="Phase mode">
          <Tag
            className="m-0"
            color={
              phasePermutationMode === "use_topology" ? "green" : "default"
            }
          >
            {phasePermutationMode}
          </Tag>
        </CardRow>
        <CardRow label="Last validation">
          <Text type="secondary" className="text-xs">
            {validationStatus?.last_validation
              ? formatDateTime(validationStatus.last_validation)
              : "N/A"}
          </Text>
        </CardRow>
      </div>
    </div>
  );
};
